import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
// 🏰 Kingdom: added arrayUnion
import { doc, getDoc, runTransaction, serverTimestamp, collection, increment, arrayUnion } from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../authContext";
import { supabase } from "./supabaseClient";
import "./EvolvePage.css";
import { getStage } from "../utils/evolveUtils";
import { FaArrowLeft } from "react-icons/fa";

// Import Assets
import SparkImg from "../assets/spark journey page.png";
import OrbitImg from "../assets/orbit journey page.png";
import GravityImg from "../assets/gravity journey page.png";
import NovaImg from "../assets/nova journey page.png";
import ConstellationImg from "../assets/constellation journey page.png";
import PushButtonImg from "../assets/push  - button.png";

const EvolvePage = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const { currentUser } = useAuth();
    const isSubmittingRef = useRef(false); // ✅ Double-click/Race protection

    const [idea, setIdea] = useState(location.state?.idea || null);
    const [evolveText, setEvolveText] = useState("");
    const [loading, setLoading] = useState(false);
    const [imageUrl, setImageUrl] = useState("");
    const [error, setError] = useState("");

    // Animation States
    const [successMessage, setSuccessMessage] = useState("");
    const [isAnimating, setIsAnimating] = useState(false);
    const [animatedStageIndex, setAnimatedStageIndex] = useState(-1);

    // Determine current stage based on evolve count
    const currentCount = idea?.evolveCount || 0;
    const currentStage = getStage(currentCount);

    // Map stage to image asset
    const getStageImage = (stage) => {
        switch (stage) {
            case "Spark": return SparkImg;
            case "Orbit": return OrbitImg;
            case "Gravity": return GravityImg;
            case "Nova": return NovaImg;
            case "Constellation": return ConstellationImg;
            default: return SparkImg;
        }
    };

    useEffect(() => {
        const fetchIdea = async () => {
            if (!idea) {
                try {
                    const postedRef = doc(db, "postedIdeas", id);
                    const postedSnap = await getDoc(postedRef);

                    if (postedSnap.exists()) {
                        setIdea({ id: postedSnap.id, ...postedSnap.data() });
                    } else {
                        const compRef = doc(db, "competitionIdeas", id);
                        const compSnap = await getDoc(compRef);
                        if (compSnap.exists()) {
                            setIdea({ id: compSnap.id, ...compSnap.data() });
                        }
                    }
                } catch (err) {
                    console.error("Error fetching idea:", err);
                    setError("Could not load idea.");
                }
            }
        };

        fetchIdea();
    }, [id, idea]);

    useEffect(() => {
        const fetchImage = async () => {
            if (idea && idea.imagePath && !imageUrl) {
                const { data } = await supabase.storage.from("images").createSignedUrl(idea.imagePath, 3600);
                if (data?.signedUrl) setImageUrl(data.signedUrl);
            }
        };
        fetchImage();
    }, [idea, imageUrl]);


    const handlePush = async () => {
        if (loading || isAnimating || isSubmittingRef.current) return;

        if (!evolveText.trim()) {
            setError("Please write something to evolve this idea.");
            return;
        }
        if (!currentUser) return;

        isSubmittingRef.current = true;
        setLoading(true);
        setError("");

        try {
            await runTransaction(db, async (transaction) => {
                // --- READS ---
                // Find the idea in any of the possible collections
                const COLLECTIONS = ["postedIdeas", "publicIdeas", "competitionIdeas", "PostedCompetitionIdeas"];
                let ideaDoc = null;
                let ideaRef = null;

                for (const coll of COLLECTIONS) {
                    const ref = doc(db, coll, id);
                    const snap = await transaction.get(ref);
                    if (snap.exists()) {
                        ideaDoc = snap;
                        ideaRef = ref;
                        break;
                    }
                }

                // ✅ CHANGED: safe check (old code crashed with a TypeError when ideaDoc was null)
                if (!ideaDoc) throw new Error("Idea does not exist!");

                const ideaData = ideaDoc.data();
                // Determine Owner
                // ✅ CHANGED: added authorId as a fallback
                const ownerId = ideaData.uid || ideaData.userId || ideaData.ownerId || ideaData.authorId;

                // 🏰 Kingdom: who has evolved this idea, and is this a new road?
                const isOwnIdea = !!ownerId && ownerId === currentUser.uid;
                const evolverIds = ideaData.evolverIds || [];
                const isNewUniqueEvolver = !isOwnIdea && !evolverIds.includes(currentUser.uid);

                // Road record for the evolver's kingdom (read now, write later)
                const roadRef = isOwnIdea ? null : doc(db, "users", currentUser.uid, "kingdomRoads", id);
                const roadSnap = roadRef ? await transaction.get(roadRef) : null;

                // --- LOGIC ---
                // We read idea mainly to calculate the NEW stage string.
                const newCount = (ideaData.evolveCount || 0) + 1;
                const newStage = getStage(newCount);

                // Create Evolve Doc Ref (Random ID)
                const evolvesCollection = collection(db, ideaRef.path, "evolves");
                const evolveRef = doc(evolvesCollection);

                // --- WRITES ---
                // 1. Update Idea (Count and Stage)
                // 🏰 Kingdom: also tracks unique evolvers (owner and repeats are skipped)
                transaction.update(ideaRef, {
                    evolveCount: newCount,
                    stage: newStage,
                    currentStage: newStage, // Added per user request schema
                    ...(isNewUniqueEvolver
                        ? { evolverIds: arrayUnion(currentUser.uid), uniqueEvolvers: increment(1) }
                        : {}),
                });

                // 2. Create Evolve Record
                transaction.set(evolveRef, {
                    evolveId: evolveRef.id,
                    userId: currentUser.uid, // Updated from uid to userId per schema
                    text: evolveText,
                    createdAt: serverTimestamp(),
                    authorName: currentUser.displayName || "Anonymous",
                    stageAtTime: newStage
                });

                // 🏰 Kingdom: first evolve of this idea grows a road in the evolver's kingdom
                if (roadRef && roadSnap && !roadSnap.exists()) {
                    transaction.set(roadRef, {
                        ideaId: id,
                        collection: ideaRef.parent.id,
                        ownerId: ownerId || null,
                        ownerName: ideaData.authorName || "",
                        title: (ideaData.title || "Untitled").slice(0, 100),
                        createdAt: serverTimestamp(),
                    });
                }

                // 3. Notify the owner in their Inbox (never runs for self-evolves)
                if (ownerId && !isOwnIdea) {
                    const messageRef = doc(collection(db, "messages"));
                    transaction.set(messageRef, {
                        to: ownerId,
                        from: "system",
                        type: "evolve",
                        content: `Someone evolved your idea "${(ideaData.title || "Untitled").slice(0, 100)}"`,
                        ideaId: id,
                        evolverId: currentUser.uid,
                        timestamp: serverTimestamp(),
                    });
                }
            });

            // --- ANIMATION ---
            setEvolveText("");
            setIsAnimating(true);

            const stages = ["Spark", "Orbit", "Gravity", "Nova", "Constellation"];
            let step = -1;

            setAnimatedStageIndex(step);

            const interval = setInterval(() => {
                step++;
                setAnimatedStageIndex(step);

                if (step >= stages.length - 1) {
                    clearInterval(interval);
                    setTimeout(() => {
                        setSuccessMessage("Idea Evolved!");
                        setTimeout(() => {
                            navigate(-1);
                        }, 2500);
                    }, 500);
                }
            }, 800);

        } catch (err) {
            console.error("Evolve transaction failed:", err);
            setError(err.message || "Failed to push evolve.");
            setLoading(false);
        } finally {
            isSubmittingRef.current = false;
        }
    };

    if (!idea) return <div className="evolve-page">Loading...</div>;

    return (
       <div className="leaderboard-container">
             <div className="leaderboard-header">
               <FaArrowLeft className="leaderboard-back-icon" onClick={() => navigate(-1)} />
               <h2 className="leaderboard-title">Evolve</h2>
             </div>

            {/* Idea Content */}
            <div className="idea-preview">
                {imageUrl && <img src={imageUrl} alt="Idea" className="idea-image-banner" />}
                <h2 className="idea-preview-title">{idea.title}</h2>
                <p className="idea-preview-description">{idea.description}</p>
            </div>

            {/* Stage Progress Map */}
            <div className="stage-progress-container">

                {/* Horizontal Stepper */}
                <div className="stage-stepper">
                    {["Spark", "Orbit", "Gravity", "Nova", "Constellation"].map((stage, index) => {
                        const stages = ["Spark", "Orbit", "Gravity", "Nova", "Constellation"];
                        const currentIndex = stages.indexOf(currentStage);

                        // If animating, use animation index. Else use real DB state.
                        const effectiveIndex = isAnimating ? animatedStageIndex : currentIndex;

                        const isCompleted = index <= effectiveIndex;
                        const isActive = stage === currentStage; // Main styling for active node?

                        return (
                            <div key={stage} className={`stepper-item ${isCompleted ? 'completed' : ''}`}>
                                <div className="step-line-container">
                                    {index < stages.length - 1 && <div className={`step-line ${index < effectiveIndex ? 'filled' : ''}`}></div>}
                                </div>
                                <div className="step-icon-wrapper">
                                    <img src={getStageImage(stage)} alt={stage} className="step-icon" />
                                </div>
                                <span className="step-label">{stage}</span>
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Input Section */}
            <div className="evolve-input-section">
                {error && <div className="error-message">{error}</div>}

                {successMessage && (
                    <div className="success-message-overlay">
                        <div className="success-content">
                            <h3>{successMessage}</h3>
                        </div>
                    </div>
                )}

                <textarea
                    className="evolve-textarea"
                    placeholder="Evolve this with your own thinking..."
                    value={evolveText}
                    onChange={(e) => setEvolveText(e.target.value)}
                    maxLength={1000}
                    disabled={loading || isAnimating}
                />

                <div className="push-btn-container" onClick={(!loading && !isAnimating) ? handlePush : undefined}>
                    {(loading || isAnimating) ? (
                        <div style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            height: '100px', // Approx button height
                            color: '#white'
                        }}>
                            <div className="spinner" style={{ width: '30px', height: '30px', border: '3px solid rgba(255,255,255,0.3)', borderTopColor: '#fff' }}></div>
                            <span style={{ marginTop: '10px', fontSize: '0.9rem', color: '#ccc' }}>Evolving...</span>
                        </div>
                    ) : (
                        <img
                            src={PushButtonImg}
                            alt="PUSH"
                            className="push-button-img"
                        />
                    )}
                </div>
            </div>
        </div>
    );
};

export default EvolvePage;